import _jsx from '@babel/runtime/helpers/jsx';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import React, { forwardRef, useRef, Children, useState, useCallback, useMemo, useEffect, cloneElement } from 'react';
import classNames from 'classnames';
import { useId } from '@andes/context';
import { VisuallyHidden } from '@andes/common';
const namespace = 'andes-segmented-control';
var namespace$1 = namespace;
const SegmentedControl = /*#__PURE__*/function () {
  const SegmentedControl = /*#__PURE__*/forwardRef((_ref, componentRef) => {
    let {
      children,
      className,
      defaultValue,
      disabled = false,
      id,
      name,
      onChange,
      srLabel,
      type = 'light',
      value,
      widthBehavior = 'variable'
    } = _ref;
    const componentId = useId(id);
    const componentName = name !== null && name !== void 0 ? name : "".concat(componentId, "-name");
    const {
      current: isControlled
    } = useRef(typeof value !== 'undefined');
    const values = Children.map(children, child => child.props.value);
    const initialValue = values.find(segmentValue => segmentValue === (isControlled ? value : defaultValue)) || values[0];
    const [uncontrolledValue, setUncontrolledValue] = useState(initialValue);
    const [selectionPosition, setSelectionPosition] = useState({
      left: 0,
      width: 0
    });
    const segmentedControlContainerRef = useRef();
    const segmentedControlElementRef = useRef(null);
    const segmentLabelsRef = useRef({});
    const enabledAnimation = useRef(false);
    const currentValue = isControlled ? value : uncontrolledValue;
    const updateSelectionPosition = useCallback(() => {
      var _selectedSegment$offs, _selectedSegment$clie;
      const selectedSegment = segmentLabelsRef.current[currentValue];
      setSelectionPosition({
        left: (_selectedSegment$offs = selectedSegment === null || selectedSegment === void 0 ? void 0 : selectedSegment.offsetLeft) !== null && _selectedSegment$offs !== void 0 ? _selectedSegment$offs : 0,
        width: (_selectedSegment$clie = selectedSegment === null || selectedSegment === void 0 ? void 0 : selectedSegment.clientWidth) !== null && _selectedSegment$clie !== void 0 ? _selectedSegment$clie : 0
      });
    }, [currentValue]);
    const resizeObserver = useMemo(() => {
      if (typeof window !== 'undefined') {
        return new ResizeObserver(entries => {
          entries.forEach(entry => {
            if (entry.target === segmentedControlElementRef.current) {
              updateSelectionPosition();
            }
          });
        });
      }
    }, [updateSelectionPosition]);
    useEffect(() => {
      if (resizeObserver && segmentedControlElementRef.current) {
        resizeObserver.observe(segmentedControlElementRef.current);
      }
      return () => {
        if (resizeObserver) resizeObserver.disconnect();
      };
    }, [resizeObserver]);
    useEffect(() => {
      updateSelectionPosition();
    }, [updateSelectionPosition]);
    const containerClassNames = classNames(namespace$1, className);
    const componentClassNames = classNames("".concat(namespace$1, "__content"), "".concat(namespace$1, "--").concat(type), "".concat(namespace$1, "--").concat(widthBehavior), {
      ["".concat(namespace$1, "--disabled-animation")]: !enabledAnimation.current
    });
    const selectedClassNames = classNames("".concat(namespace$1, "__selection"), {
      ["".concat(namespace$1, "__selection--enabled-animation")]: enabledAnimation.current
    });
    const segmentClickHandler = event => {
      if (!isControlled) setUncontrolledValue(event.target.value);
      if (typeof onChange === 'function') onChange(event);
    };
    useEffect(() => () => {
      enabledAnimation.current = true;
    }, [currentValue]);
    return /*#__PURE__*/React.createElement("div", {
      id: componentId,
      role: "radiogroup",
      className: containerClassNames,
      "aria-label": srLabel,
      ref: el => {
        [segmentedControlContainerRef, componentRef].forEach(ref => {
          if (typeof ref === 'function') {
            ref(el);
          } else if (ref) {
            ref.current = el;
          }
        });
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: componentClassNames,
      ref: segmentedControlElementRef
    }, Children.map(children, child => child && child.type.displayName === 'Segment' && /*#__PURE__*/cloneElement(child, {
      segmentLabelsRef,
      id: componentId,
      name: componentName,
      disabled: disabled || child.props.disabled,
      selected: child.props.value === currentValue,
      onClick: event => {
        segmentClickHandler(event);
        if (typeof child.props.onClick === 'function') {
          child.props.onClick(event);
        }
      }
    })), /*#__PURE__*/_jsx("div", {
      className: selectedClassNames,
      style: _objectSpread({}, selectionPosition)
    })));
  });
  SegmentedControl.displayName = 'SegmentedControl';
  return SegmentedControl;
}();
var SegmentedControl$1 = SegmentedControl;
const segmentNamespace = "".concat(namespace$1, "__segment");
const Segment = /*#__PURE__*/function () {
  const Segment = /*#__PURE__*/forwardRef((_ref, ref) => {
    let {
      className,
      disabled = false,
      icon,
      id,
      name,
      onClick,
      segmentLabelsRef,
      selected = false,
      srLabel,
      text,
      value
    } = _ref;
    const componentClassNames = classNames(segmentNamespace, {
      ["".concat(segmentNamespace, "--with-text")]: !!text,
      ["".concat(segmentNamespace, "--with-icon")]: !!icon
    }, className);
    const inputId = "".concat(id, "-segment-input-").concat(value);
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("input", {
      className: "andes-visually-hidden",
      type: "radio",
      id: inputId,
      name: name,
      value: value,
      ref: ref,
      onChange: onClick,
      disabled: disabled,
      checked: selected
    }), /*#__PURE__*/React.createElement("label", {
      ref: el => {
        if (el && segmentLabelsRef) segmentLabelsRef.current[value] = el;
      },
      className: componentClassNames,
      htmlFor: inputId
    }, icon && /*#__PURE__*/React.createElement(React.Fragment, null, srLabel && /*#__PURE__*/_jsx(VisuallyHidden, {}, void 0, srLabel), icon), text));
  });
  Segment.displayName = 'Segment';
  return Segment;
}();
var Segment$1 = Segment;
export { Segment$1 as Segment, SegmentedControl$1 as SegmentedControl };
